import React from 'react';
import { Stethoscope, ExternalLink, Phone, Mail, Users, ArrowRight, Shield, CheckCircle } from 'lucide-react';

const ClinicianAccessSection = ({ isDark }) => {
  const contacts = [
    {
      name: 'Anusha Bhupati',
      role: 'Enterprise Lead - India',
      phone: '+91 87909 19141',
      email: 'anusha.bhupati@exoralabs.com'
    },
    {
      name: 'Nandini K',
      role: 'Clinical Solutions - India',
      phone: '+91 97313 06219',
      email: 'nandini.k@exoralabs.com'
    },
    {
      name: 'Dr. Rohith G',
      role: 'Medical Director - UK & Global',
      phone: '+44 7921 398886',
      email: 'drrohithg@gmail.com'
    }
  ];

  const features = [
    'Test with real patient data in secure environment',
    'Full access to Clinical Copilot features',
    'Real-time AI documentation and coding',
    'Evidence-based decision support'
  ];

  return (
    <section 
      id="clinician-access" 
      className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-gradient-to-b from-blue-50 to-white'}`}
      data-testid="clinician-access-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
          }`}>
            For Clinicians & Doctors
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            Try SkyDoc with Real Patients
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            Ready to experience SkyDoc in your clinical practice? Get login access to test our AI-powered platform with real patient encounters.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left - Login Access Card */}
          <div className={`rounded-2xl border p-8 ${
            isDark 
              ? 'bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700' 
              : 'bg-white border-slate-200 shadow-lg'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <Stethoscope className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Clinician Portal Access
                </h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Secure login for healthcare professionals
                </p>
              </div>
            </div>

            {/* Features List */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{feature}</span>
                </div>
              ))}
            </div>

            {/* Login Button */}
            <a
              href="https://www.skydoc.ai/login"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full inline-flex items-center justify-center gap-2 text-lg py-4"
              data-testid="clinician-login-btn"
            >
              Access Clinician Portal
              <ExternalLink className="w-5 h-5" />
            </a>

            {/* Security Note */}
            <div className={`mt-6 flex items-start gap-3 p-4 rounded-lg ${
              isDark ? 'bg-slate-700/50' : 'bg-slate-50'
            }`}>
              <Shield className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Secure, HIPAA-compliant access. Your patient data is protected with enterprise-grade encryption.
              </p>
            </div>
          </div>

          {/* Right - Contact for Access */}
          <div>
            <div className={`rounded-2xl border p-8 ${
              isDark 
                ? 'bg-slate-900 border-slate-700' 
                : 'bg-slate-50 border-slate-200'
            }`}>
              <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Request Login Access & Discuss Pricing
              </h3>
              <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Contact our team to get your credentials and discuss enterprise pricing options for your clinic or hospital.
              </p>

              {/* Contact Cards */}
              <div className="space-y-4">
                {contacts.map((contact, index) => (
                  <div 
                    key={index}
                    className={`p-4 rounded-xl border ${
                      isDark 
                        ? 'bg-slate-800 border-slate-700 hover:border-blue-600' 
                        : 'bg-white border-slate-200 hover:border-blue-400'
                    } transition-colors`}
                    data-testid={`contact-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isDark ? 'bg-slate-700' : 'bg-slate-100'
                      }`}>
                        <Users className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {contact.name}
                        </p>
                        <p className={`text-sm mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {contact.role}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <a 
                            href={`tel:${contact.phone.replace(/\s/g, '')}`}
                            className="inline-flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-400"
                          >
                            <Phone className="w-4 h-4" />
                            {contact.phone}
                          </a>
                          <a 
                            href={`mailto:${contact.email}`}
                            className="inline-flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-400"
                          >
                            <Mail className="w-4 h-4" />
                            Email
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Text */}
              <div className={`mt-6 p-4 rounded-lg text-center ${
                isDark ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'
              }`}>
                <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                  <strong>Call or email any of our contacts</strong> to request login credentials and discuss pricing tailored to your practice size.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClinicianAccessSection;
